/**
 * Clés API (agents / MCP) — émission, listage, révocation.
 *
 * Ces clés branchent un agent (Claude Code, Claude Desktop…) sur exiPlan via `mcp/`, dont le
 * `README.md` fait foi pour l'installation et le modèle de droits. Une clé ne porte AUCUN droit
 * propre : elle ne fait que restreindre ce que son propriétaire possède déjà (`resolveAccess()`
 * côté serveur tranche à chaque requête) — c'est pourquoi l'écran ne demande jamais de rôle,
 * seulement des portées.
 *
 * Point le plus important de cet écran : le jeton en clair n'est rendu QU'à la création
 * (`POST /me/api-keys`). Il n'est pas stocké côté serveur — seul son SHA-256 l'est — donc le
 * produit est structurellement incapable de le réafficher. `issuedToken` ne vit que dans l'état
 * local de ce composant ; fermer la boîte le perd définitivement, comme le veut le serveur.
 *
 * « Nouvelle clé » n'est PAS dans ce panneau : c'est le primaire de l'en-tête de la page Compte
 * sur cet onglet (règle de cohérence 1.3.0 d'`@exidis/ui` — un seul primaire par écran, dans
 * l'en-tête). `AccountPage` pilote donc l'ouverture de la modale (`creating`/`onCreatingChange`).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_KEY_SCOPES } from '@exiplan/shared-types';
import { API_BASE, api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert, appConfirm } from '../dialogs/service.js';
import { Empty, Eyebrow, Panel } from './common.jsx';
import { Modal } from './Modal.jsx';
import { CopyButton } from './CopyButton.jsx';
import { buildApiKeyUsageSnippet } from '../lib/mcp-snippets.js';
import {
  BTN_GHOST_SM,
  BTN_PRIMARY_SM,
  CHECK,
  CHOIX_LABEL,
  FORM_FIELD,
  FORM_LABEL,
  INPUT,
  TAG,
  TD,
  TH,
} from '../ui/theme.js';

/**
 * Clés de libellé/description miroir de `API_KEY_SCOPES` — un `<label>` ne peut pas porter le
 * code brut seul. Exportée : `McpHelpPanel` réutilise les mêmes libellés (§2.1 de la spec) plutôt
 * que d'en tenir une seconde copie.
 */
export const SCOPE_I18N_KEYS = {
  'projects:read': 'apiKeys.scopes.projectsRead',
  'tasks:read': 'apiKeys.scopes.tasksRead',
  'tasks:write': 'apiKeys.scopes.tasksWrite',
  'stats:read': 'apiKeys.scopes.statsRead',
};

const DEFAULT_TTL_DAYS = 90;

function formatDateTime(iso, locale) {
  if (!iso) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

/** Formulaire de création — isolé pour garder `ApiKeysPanel` lisible. */
function CreateKeyForm({ onCreated, onCancel }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(() => new Set());
  const [ttlDays, setTtlDays] = useState(DEFAULT_TTL_DAYS);
  const [busy, setBusy] = useState(false);

  const toggleScope = (scope) =>
    setScopes((current) => {
      const next = new Set(current);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });

  const canSubmit = name.trim().length > 0 && scopes.size > 0 && !busy;

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post('/me/api-keys', {
        name: name.trim(),
        scopes: [...scopes],
        ttlDays: Number(ttlDays) || DEFAULT_TTL_DAYS,
      });
      onCreated(data);
    } catch (error) {
      await appAlert(t('apiKeys.createFailed', { message: error.message }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label className={FORM_FIELD}>
        <span className={FORM_LABEL}>{t('apiKeys.nameLabel')}</span>
        <input
          className={INPUT}
          placeholder={t('apiKeys.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={busy}
        />
      </label>

      <fieldset className="flex flex-col gap-2">
        <legend className={`${FORM_LABEL} mb-2`}>{t('apiKeys.scopesLegend')}</legend>
        {API_KEY_SCOPES.map((scope) => {
          const keys = SCOPE_I18N_KEYS[scope];
          const checked = scopes.has(scope);
          return (
            <label key={scope} className={`${CHOIX_LABEL} items-start`}>
              <input
                type="checkbox"
                className={`${CHECK} mt-0.5`}
                checked={checked}
                disabled={busy}
                onChange={() => toggleScope(scope)}
              />
              <span>
                <span className="block font-semibold">{t(`${keys}.label`)}</span>
                <span className="block text-xs text-ink-faint">{t(`${keys}.description`)}</span>
                {scope === 'tasks:write' && checked && (
                  <span
                    role="alert"
                    className="mt-1 block border-2 border-accent-trait bg-accent-wash px-2 py-1 text-xs text-accent-ink"
                  >
                    {t('apiKeys.scopes.tasksWrite.warning')}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </fieldset>

      <label className={FORM_FIELD}>
        <span className={FORM_LABEL}>{t('apiKeys.ttlLabel')}</span>
        <input
          type="number"
          min={1}
          max={365}
          className={`${INPUT} w-24`}
          value={ttlDays}
          onChange={(event) => setTtlDays(event.target.value)}
          disabled={busy}
        />
      </label>

      <div className="flex items-center gap-2">
        <button type="submit" className={BTN_PRIMARY_SM} disabled={!canSubmit}>
          {t('apiKeys.create')}
        </button>
        <button type="button" className={BTN_GHOST_SM} onClick={onCancel} disabled={busy}>
          {t('apiKeys.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.creating Modale de création ouverte — ouverte par le primaire d'en-tête.
 * @param {(open: boolean) => void} props.onCreatingChange
 */
export function ApiKeysPanel({ creating, onCreatingChange }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  const {
    data: response,
    loading,
    error,
    reload,
  } = useAsync((signal) => api.get('/me/api-keys', { signal }), []);
  // Garde de forme, pas seulement de véracité (même piège que `usePreferences`) : un mock de
  // test trop permissif, ou une réponse mal formée, ne doit pas faire planter la table.
  const keys = Array.isArray(response?.data) ? response.data : [];

  const scopeLabel = useMemo(
    () => (scope) => (SCOPE_I18N_KEYS[scope] ? t(`${SCOPE_I18N_KEYS[scope]}.label`) : scope),
    [t],
  );

  const onCreated = (created) => {
    setIssued(created);
    reload();
  };

  const closeDialog = () => {
    onCreatingChange(false);
    // Le jeton n'a jamais existé qu'ici : fermer la boîte le perd, exactement comme le serveur
    // le veut (il n'a de toute façon rien à réafficher).
    setIssued(null);
    setCopied(false);
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const revoke = async (key) => {
    if (!(await appConfirm(t('apiKeys.revokeConfirm', { name: key.name })))) return;
    try {
      await api.delete(`/me/api-keys/${key.id}`);
      await reload();
    } catch (error) {
      await appAlert(t('apiKeys.revokeFailed', { message: error.message }));
    }
  };

  return (
    <Panel>
      <div>
        <div>
          <Eyebrow>{t('apiKeys.eyebrow')}</Eyebrow>
          <p className="mt-2 max-w-prose text-sm text-ink-soft">
            {t('apiKeys.introPrefix')}
            <Link to="/compte?onglet=aide-mcp" className="text-accent-ink underline">
              {t('apiKeys.introLinkLabel')}
            </Link>
            {t('apiKeys.introSuffix')}
          </p>
          <p className="mt-1 max-w-prose text-xs text-ink-faint">{t('apiKeys.scopeReminder')}</p>
        </div>
      </div>

      {/* Même garde qu'ailleurs (ListView, ShareProjectDialog) : `loading` protège l'état vide
          d'un affichage trompeur le temps que la réponse arrive. */}
      {loading ? (
        <p className="mt-4 text-sm text-ink-soft">{t('apiKeys.loading')}</p>
      ) : error ? (
        <div className="mt-4">
          <Empty>{t('apiKeys.loadError', { message: error.message })}</Empty>
        </div>
      ) : keys.length === 0 ? (
        <div className="mt-4">
          <Empty>{t('apiKeys.empty')}</Empty>
        </div>
      ) : (
        <table className="mt-4 w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}>{t('apiKeys.columnName')}</th>
              <th className={TH}>{t('apiKeys.columnPrefix')}</th>
              <th className={TH}>{t('apiKeys.columnScopes')}</th>
              <th className={TH}>{t('apiKeys.columnExpires')}</th>
              <th className={TH}>{t('apiKeys.columnLastUsed')}</th>
              <th className={TH}>{t('apiKeys.columnStatus')}</th>
              <th className={TH}>{t('apiKeys.columnActions')}</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => {
              const revoked = Boolean(key.revoked ?? key.revokedAt);
              return (
                <tr key={key.id}>
                  <td className={TD}>{key.name}</td>
                  <td className={TD}>
                    <span className="font-mono text-[11px] text-ink-faint">{key.prefix}</span>
                  </td>
                  <td className={TD}>
                    <div className="flex flex-wrap gap-1">
                      {key.scopes.map((scope) => (
                        <span key={scope} className={TAG}>
                          {scopeLabel(scope)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={TD}>{formatDateTime(key.expiresAt, locale) ?? '—'}</td>
                  <td className={TD}>
                    {formatDateTime(key.lastUsedAt, locale) ?? t('apiKeys.neverUsed')}
                  </td>
                  <td className={TD}>
                    {/* La couleur ne porte jamais seule l'information : icône + libellé. */}
                    <span
                      className={`${TAG} ${
                        revoked
                          ? 'border-line text-ink-faint'
                          : 'border-accent-trait text-accent-ink'
                      }`}
                    >
                      <span aria-hidden="true">{revoked ? '✕' : '●'}</span>
                      {revoked ? t('apiKeys.statusRevoked') : t('apiKeys.statusActive')}
                    </span>
                  </td>
                  <td className={TD}>
                    {!revoked && (
                      <button type="button" className={BTN_GHOST_SM} onClick={() => revoke(key)}>
                        {t('apiKeys.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {creating && (
        <Modal title={t('apiKeys.createTitle')} onClose={closeDialog}>
          {issued ? (
            <div className="flex flex-col gap-3">
              <div className="border-2 border-accent-trait bg-accent-wash p-3">
                <p role="alert" className="text-sm font-semibold text-accent-ink">
                  {t('apiKeys.tokenWarningTitle')}
                </p>
                <p className="mt-1 text-sm text-accent-ink">{t('apiKeys.tokenWarning')}</p>
                <p className="mt-2 break-all font-mono text-[11px] text-ink">{issued.token}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" className={BTN_GHOST_SM} onClick={copyToken}>
                    {t('apiKeys.copyToken')}
                  </button>
                  {copied && (
                    <span className="text-xs text-ink-soft">{t('apiKeys.tokenCopied')}</span>
                  )}
                </div>
              </div>

              {/* Clé d'agent AUTONOME (standard MCP § 8, MCP-K1..K6) — pas un client OAuth :
                  jeton réellement émis, seul moment où il existe en clair côté client. Rien
                  n'est persisté : la fermeture de cette boîte (`closeDialog`) vide `issued`,
                  donc ce snippet disparaît avec lui. */}
              <div className="flex flex-col gap-3 border-t-2 border-line pt-3">
                <p className="text-sm font-semibold text-ink">{t('apiKeys.mcpSnippetsTitle')}</p>
                {(() => {
                  // Un curl copié-collé a besoin d'une URL absolue : `/api` seul (chemin
                  // relatif, correct pour du `fetch` dans le navigateur) ne veut rien dire hors
                  // contexte. `API_BASE` est déjà absolue quand elle est posée (§ lib/api.js) ;
                  // sinon l'origine courante du navigateur est la seule base connue.
                  const snippet = buildApiKeyUsageSnippet({
                    apiUrl: `${API_BASE || window.location.origin}/api`,
                    token: issued.token,
                  });
                  return (
                    <div className="flex flex-col gap-1">
                      <pre className="overflow-x-auto border-2 border-line bg-paper p-2 font-mono text-[11px] text-ink">
                        {snippet}
                      </pre>
                      <CopyButton value={snippet} className="self-start" />
                    </div>
                  );
                })()}
              </div>

              <button type="button" className={BTN_PRIMARY_SM} onClick={closeDialog}>
                {t('apiKeys.close')}
              </button>
            </div>
          ) : (
            <CreateKeyForm onCreated={onCreated} onCancel={closeDialog} />
          )}
        </Modal>
      )}
    </Panel>
  );
}
